import { ChangeEvent, useEffect, useState } from 'react';

import { Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { cn } from '@/lib/utils';

export type DateRangePreset = '24h' | '7d' | '30d' | 'all';

const PRESETS: { value: DateRangePreset; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: 'all', label: 'All' },
];

type ToolbarProps = {
  search: string;
  onSearchChange: (value: string) => void;
  dateRange: DateRangePreset;
  onDateRangeChange: (value: DateRangePreset) => void;
};

/**
 * Search input is debounced (~300 ms) so each keystroke doesn't fire a
 * server-side fetch. Both the search and the date-range preset are URL-backed
 * upstream so filtered views are shareable.
 */
export function Toolbar({
  search,
  onSearchChange,
  dateRange,
  onDateRangeChange,
}: ToolbarProps) {
  const [local, setLocal] = useState(search);
  useEffect(() => setLocal(search), [search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (local !== search) onSearchChange(local);
    }, 300);
    return () => clearTimeout(t);
  }, [local, search, onSearchChange]);

  const onInput = (e: ChangeEvent<HTMLInputElement>) =>
    setLocal(e.target.value);
  const clear = () => {
    setLocal('');
    onSearchChange('');
  };

  return (
    <div className="bg-background border-border flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
      <InputGroup className="w-72">
        <InputGroupAddon align="inline-start">
          <Search />
        </InputGroupAddon>
        <InputGroupInput
          type="search"
          value={local}
          onChange={onInput}
          placeholder="Search user, column, or value..."
          aria-label="Search audit history"
        />
        {local.length > 0 && (
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              variant="ghost"
              size="icon-xs"
              onClick={clear}
              aria-label="Clear search"
            >
              <X />
            </InputGroupButton>
          </InputGroupAddon>
        )}
      </InputGroup>

      <div
        role="tablist"
        aria-label="Date range"
        className="bg-muted text-muted-foreground inline-flex items-center rounded-lg p-0.5"
      >
        {PRESETS.map((p) => {
          const active = p.value === dateRange;
          return (
            <Button
              key={p.value}
              role="tab"
              aria-selected={active}
              variant="ghost"
              size="sm"
              className={cn(
                'h-7 rounded-md px-3 text-xs',
                active &&
                  'bg-background text-foreground hover:bg-background shadow-sm',
              )}
              onClick={() => onDateRangeChange(p.value)}
            >
              {p.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
